/**
 * Verb ids, the scoped subsets of them, and the analytics wire values. Strings only.
 *
 * Split out of `verbs.tsx` on purpose: that file carries glyphs, so it imports `NAV_ICONS` from
 * `@pages` and pulls React components and MUI icons with it. `src/stores/Tracking.ts` needs the
 * id vocabulary but is constructed at app boot, so dragging the nav's icon module into it would
 * cost startup work for nothing. This file imports NOTHING, so a store can read it freely.
 *
 * Import from here in stores. Import from `./verbs` when you need a label or a glyph.
 */

/**
 * Something the user does TO a library or a media item. One spelling per verb — the app used
 * to carry both `work-with-agent` and `ask-an-agent` for the same action, which is how the
 * same button ended up reported under two names.
 */
export type VerbId = 'work-with-agent' | 'find-moments' | 'add-media' | 'clip-podcast';

/**
 * The library-scoped verbs — exactly what `useLibraryVerbs` can dispatch. `add-media` is
 * library-scoped too but isn't here: it's a rail-only affordance owned by
 * `buildLibraryRailItems`, and no menu or tracking event carries it.
 *
 * `satisfies readonly VerbId[]` is what makes this load-bearing rather than decorative: rename
 * a verb in `VerbId` and this tuple fails to compile, which is where you want to find out.
 * Deriving the union from the tuple then propagates that break to every consumer.
 *
 * This replaced six hand-written `Extract<VerbId, 'work-with-agent' | 'find-moments'>` unions
 * whose comments claimed exactly this guarantee and did not provide it — `Extract` over a
 * member that no longer exists quietly yields `never`, so a rename would have silently emptied
 * all six and errored only at whichever call site happened to compare against a literal.
 */
export const LIBRARY_VERB_IDS = [
  'work-with-agent',
  'find-moments',
] as const satisfies readonly VerbId[];
export type LibraryVerbId = (typeof LIBRARY_VERB_IDS)[number];

/**
 * The media-scoped verbs, in the order every surface shows them: the grid card's rail, the table
 * row's rail, and the moment modal's action bar — where the leading verb is promoted to the CTA
 * and the rest keep this order in the kebab (`splitMediaActions`).
 *
 * `VideoMenuButton`'s desktop kebab is the exception and carries none of them, because every
 * surface mounting it shows them alongside.
 *
 * Order lives here because all those surfaces must agree on it. Labels and glyphs come from
 * `./verbs`; whether a given verb is offered at all comes from `useMediaVerbs`' `actions` (the
 * `CHAT_URL` gate and the still-image check on clipping).
 */
export const MEDIA_VERB_IDS = [
  'work-with-agent',
  'find-moments',
  'clip-podcast',
] as const satisfies readonly VerbId[];
export type MediaVerbId = (typeof MEDIA_VERB_IDS)[number];

/**
 * Verbs whose id differs from what the two *menu* analytics events emit.
 *
 * `Library Menu Action` and `Media Menu Action` predate the rename and have always sent
 * `ask-an-agent` for the agent verb; `Library Verb Clicked` is newer and sends the code id.
 * All three are live in dashboards, so none of them may change — something has to translate,
 * and the serialization boundary is where it belongs.
 *
 * `Partial` because every other verb's id already is its wire value.
 */
const LEGACY_MENU_ACTION_ID: Partial<Record<VerbId, string>> = {
  'work-with-agent': 'ask-an-agent',
};

/**
 * Translate an action id to the value the two menu events expect. Non-verb actions (`rename`,
 * `delete`, `pin`, `download`, …) pass through unchanged, which is why this is generic over
 * `string` rather than taking `VerbId`.
 *
 * Named for the RULE, not the id: the rule is "these two events emit the legacy value", so a
 * future event that wants the current id must simply not call this. An earlier name
 * (`verbAnalyticsId`) read as "the analytics id for a verb", which invited exactly the wrong
 * call — and a wrong call here produces a silently wrong dashboard with no compile error and no
 * runtime symptom.
 */
export const legacyMenuActionId = (action: string): string =>
  LEGACY_MENU_ACTION_ID[action as VerbId] ?? action;

/**
 * Type guards for "is this menu action one of the verbs", used by the kebabs and drawers that
 * filter the verbs out when a rail already shows them.
 *
 * Guards rather than bare `.includes` calls at each site: `LIBRARY_VERB_IDS.includes(x)` won't
 * accept the wider `LibraryMenuAction`, so every call site was casting — `a.id as LibraryVerbId`,
 * an assertion about the exact value the filter is trying to test. Inert today only because
 * `includes` never dereferences it, and it would keep compiling straight through a rename.
 */
export const isLibraryVerbId = (id: string): id is LibraryVerbId =>
  (LIBRARY_VERB_IDS as readonly string[]).includes(id);

export const isMediaVerbId = (id: string): id is MediaVerbId =>
  (MEDIA_VERB_IDS as readonly string[]).includes(id);
